import React, { useState, useEffect, useCallback } from 'react';
import {
  FiActivity, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiShield, FiFilter, FiX,
} from 'react-icons/fi';
import { buscarLogs } from '../../services/auditoria';
import { buscarMinhasPermissoes } from '../../services/permissoes';
import styles from './Auditoria.module.css';

/* ===== Helpers ===== */

const ACAO_CONFIG = {
  criacao:       { label: 'Criação',       css: 'acaoCriacao' },
  atualizacao:   { label: 'Atualização',   css: 'acaoAtualizacao' },
  exclusao:      { label: 'Exclusão',      css: 'acaoExclusao' },
  visualizacao:  { label: 'Visualização',  css: 'acaoVisualizacao' },
  navegacao:     { label: 'Navegação',     css: 'acaoNavegacao' },
  login:         { label: 'Login',         css: 'acaoLogin' },
  logout:        { label: 'Logout',        css: 'acaoLogout' },
  erro:          { label: 'Erro',          css: 'acaoErro' },
  acesso_negado: { label: 'Acesso negado', css: 'acaoAcessoNegado' },
};

function formatarData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function AcaoBadge({ acao }) {
  const cfg = ACAO_CONFIG[acao] || { label: acao, css: 'acaoOutro' };
  return (
    <span className={`${styles.acaoBadge} ${styles[cfg.css]}`}>
      {cfg.label}
    </span>
  );
}

function resumirDetalhes(detalhes) {
  if (!detalhes || !Object.keys(detalhes).length) return '—';
  const { method, path, status, page } = detalhes;
  if (method && path) return `${method} ${path} → ${status}`;
  if (page) return `→ ${page}`;
  return JSON.stringify(detalhes).slice(0, 60);
}

const MODULOS = [
  'Leads', 'Clientes', 'Onboard', 'Etapas Padrão', 'Usuários',
  'Permissões', 'Configurações', 'Autenticação', 'Vencimentos',
];
const ACOES = Object.keys(ACAO_CONFIG);
const LIMITE = 100;

const FILTROS_VAZIOS = { modulo: '', acao: '', data_inicio: '', data_fim: '' };

/* ===== Módulo principal ===== */

function Auditoria() {
  const [permissoes,    setPermissoes]    = useState(() => {
    try { const c = localStorage.getItem('erp_perm'); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [logs,          setLogs]          = useState([]);
  const [total,         setTotal]         = useState(0);
  const [pagina,        setPagina]        = useState(1);
  const [carregando,    setCarregando]    = useState(false);
  const [erro,          setErro]          = useState('');
  const [filtros,       setFiltros]       = useState(FILTROS_VAZIOS);
  const [filtrosAtivos, setFiltrosAtivos] = useState(FILTROS_VAZIOS);

  /* Verifica acesso — só admin e gerente */
  const podeVer = permissoes === null /* ainda carregando, nega até confirmar */
    ? false
    : permissoes.admin || permissoes.tipo === 'gerente' || permissoes.tipo === 'admin';

  useEffect(() => {
    buscarMinhasPermissoes()
      .then(p => {
        setPermissoes(p);
        try { localStorage.setItem('erp_perm', JSON.stringify(p)); } catch {}
      })
      .catch(() => {});
  }, []);

  const totalPaginas = Math.max(1, Math.ceil(total / LIMITE));

  const carregar = useCallback(async (pg = 1, f = filtrosAtivos) => {
    setCarregando(true);
    setErro('');
    try {
      const res = await buscarLogs({
        page:        pg,
        limite:      LIMITE,
        modulo:      f.modulo      || undefined,
        acao:        f.acao        || undefined,
        data_inicio: f.data_inicio || undefined,
        data_fim:    f.data_fim    || undefined,
      });
      setLogs(res.logs || []);
      setTotal(res.total || 0);
      setPagina(pg);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [filtrosAtivos]);

  useEffect(() => {
    if (podeVer) carregar(1, FILTROS_VAZIOS);
  }, [podeVer]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFiltrar(e) {
    e.preventDefault();
    setFiltrosAtivos(filtros);
    carregar(1, filtros);
  }

  function handleLimpar() {
    setFiltros(FILTROS_VAZIOS);
    setFiltrosAtivos(FILTROS_VAZIOS);
    carregar(1, FILTROS_VAZIOS);
  }

  function handlePagina(nova) {
    carregar(nova);
  }

  if (!podeVer) {
    return (
      <div className={styles.acessoNegado}>
        <FiShield size={32} />
        <p className={styles.acessoNegadoTitulo}>Acesso Restrito</p>
        <p>Esta área é exclusiva para administradores e gerentes.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>

      {/* Cabeçalho */}
      <div className={styles.header}>
        <div className={styles.headerIcone}><FiActivity size={18} /></div>
        <div>
          <h1 className={styles.titulo}>Auditoria</h1>
          <p className={styles.subtitulo}>
            {total.toLocaleString('pt-BR')} eventos registrados
          </p>
        </div>
      </div>

      {/* Filtros */}
      <form className={styles.filtros} onSubmit={handleFiltrar}>
        <div className={styles.filtroGrupo}>
          <span className={styles.filtroLabel}>De</span>
          <input
            type="date"
            className={styles.filtroInput}
            value={filtros.data_inicio}
            onChange={e => setFiltros(p => ({ ...p, data_inicio: e.target.value }))}
          />
        </div>
        <div className={styles.filtroGrupo}>
          <span className={styles.filtroLabel}>Até</span>
          <input
            type="date"
            className={styles.filtroInput}
            value={filtros.data_fim}
            onChange={e => setFiltros(p => ({ ...p, data_fim: e.target.value }))}
          />
        </div>
        <div className={styles.filtroGrupo}>
          <span className={styles.filtroLabel}>Módulo</span>
          <select
            className={styles.filtroSelect}
            value={filtros.modulo}
            onChange={e => setFiltros(p => ({ ...p, modulo: e.target.value }))}
          >
            <option value="">Todos</option>
            {MODULOS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className={styles.filtroGrupo}>
          <span className={styles.filtroLabel}>Ação</span>
          <select
            className={styles.filtroSelect}
            value={filtros.acao}
            onChange={e => setFiltros(p => ({ ...p, acao: e.target.value }))}
          >
            <option value="">Todas</option>
            {ACOES.map(a => <option key={a} value={a}>{ACAO_CONFIG[a].label}</option>)}
          </select>
        </div>
        <button type="submit" className={styles.btnFiltrar}>
          <FiFilter size={13} /> Filtrar
        </button>
        <button type="button" className={styles.btnLimpar} onClick={handleLimpar}>
          <FiX size={13} /> Limpar
        </button>
      </form>

      {/* Barra de resultados */}
      <div className={styles.resultadosBarra}>
        <span className={styles.resultadosInfo}>
          {carregando ? 'Carregando...' : `${logs.length} de ${total.toLocaleString('pt-BR')} eventos`}
        </span>
        <button
          type="button"
          className={styles.btnRefresh}
          onClick={() => carregar(pagina)}
          disabled={carregando}
        >
          <FiRefreshCw size={13} className={carregando ? styles.btnRefreshGirando : ''} />
          Atualizar
        </button>
      </div>

      {/* Tabela */}
      <div className={styles.tabelaWrap}>
        {erro ? (
          <p className={styles.vazio}>{erro}</p>
        ) : logs.length === 0 && !carregando ? (
          <p className={styles.vazio}>Nenhum evento encontrado.</p>
        ) : (
          <>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th className={styles.colData}>Data / Hora</th>
                  <th className={styles.colUsuario}>Usuário</th>
                  <th className={styles.colAcao}>Ação</th>
                  <th className={styles.colModulo}>Módulo</th>
                  <th className={styles.colDetalhes}>Detalhes</th>
                  <th className={styles.colIp}>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className={styles.colData}>
                      <span className={styles.timestamp}>{formatarData(log.criado_em)}</span>
                    </td>
                    <td className={styles.colUsuario}>
                      <span className={styles.usuarioEmail}>
                        {log.usuario_email || '—'}
                      </span>
                    </td>
                    <td className={styles.colAcao}>
                      <AcaoBadge acao={log.acao} />
                    </td>
                    <td className={styles.colModulo}>
                      {log.modulo
                        ? <span className={styles.moduloBadge}>{log.modulo}</span>
                        : '—'}
                    </td>
                    <td className={styles.colDetalhes}>
                      <span className={styles.detalhesTexto} title={JSON.stringify(log.detalhes)}>
                        {resumirDetalhes(log.detalhes)}
                      </span>
                    </td>
                    <td className={styles.colIp}>{log.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalPaginas > 1 && (
              <div className={styles.paginacao}>
                <button
                  className={styles.btnPagina}
                  onClick={() => handlePagina(pagina - 1)}
                  disabled={pagina <= 1 || carregando}
                >
                  <FiChevronLeft size={14} /> Anterior
                </button>
                <span className={styles.paginaInfo}>
                  Página {pagina} de {totalPaginas}
                </span>
                <button
                  className={styles.btnPagina}
                  onClick={() => handlePagina(pagina + 1)}
                  disabled={pagina >= totalPaginas || carregando}
                >
                  Próxima <FiChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}

export default Auditoria;
